// «Saldo de regalo» en la ficha del cliente (14-oct-2026, Marrieto #1400). Son PESOS que llegaron por
// gift cards en modo dinero y que se gastan como forma de pago «Gift card» en la caja Lealify; van
// aparte de los puntos y no canjean recompensas. Datos: /giftcards/balance/:id.
// No pinta nada si el servidor aún no tiene la función o si el negocio nunca lo usó (modo puntos,
// saldo 0 y sin movimientos). «Cargar» = carga manual (p. ej. pasar a pesos una gift card vieja en
// puntos); el servidor exige permiso de gestión de gift cards.
import React, { useCallback, useEffect, useState } from 'react';
import { Gift, Loader2, Plus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { giftcardsApi, type GiftBalanceMovement, type GiftCardMode } from '../../../api/giftcardsApi';

const pesos = (n: number) => `$${(Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fecha = (s: string) => String(s || '').slice(0, 10).split('-').reverse().join('/');
const TIPO: Record<GiftBalanceMovement['type'], string> = { carga: 'Carga', cobro: 'Pago en caja', reverso: 'Devolución' };

const GiftBalanceCard: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [data, setData] = useState<{ mode: GiftCardMode; balance: number; movements: GiftBalanceMovement[] } | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [monto, setMonto] = useState('');
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const r = await giftcardsApi.balance(clientId);
      setData(r.available ? { mode: r.mode || 'points', balance: Number(r.balance) || 0, movements: r.movements || [] } : null);
    } catch { setData(null); }   // sin permiso del módulo o sin la función: no se pinta
  }, [clientId]);
  useEffect(() => { cargar(); }, [cargar]);

  if (!data) return null;
  if (data.mode !== 'money' && data.balance === 0 && data.movements.length === 0) return null;

  const guardar = async () => {
    const n = parseFloat(monto);
    if (!(n > 0)) { toast.error('Escribe un monto mayor a 0'); return; }
    setGuardando(true);
    try {
      await giftcardsApi.loadBalance(clientId, Math.round(n * 100) / 100, nota.trim() || undefined);
      toast.success(`Se cargaron ${pesos(n)} al saldo de regalo`);
      setMonto(''); setNota(''); setAbierto(false);
      cargar();
    } catch (e: any) {
      toast.error(e?.message || 'No se pudo cargar el saldo');
    } finally { setGuardando(false); }
  };

  return (
    <div className="mt-4 pt-3 border-t border-line" data-testid="gift-balance-card">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-content-2 flex items-center">
          <Gift className="h-4 w-4 mr-2 text-teal-500" />Saldo de regalo
        </span>
        <span className="text-lg font-bold text-teal-700 dark:text-teal-300">{pesos(data.balance)}</span>
      </div>
      <p className="text-xs text-content-3 mt-0.5">Pesos de gift cards: se usan para pagar en la caja con «Gift card». No son puntos.</p>

      {data.movements.length > 0 && (
        <ul className="mt-2 space-y-1">
          {data.movements.slice(0, 5).map((m) => (
            <li key={m.id} className="flex justify-between text-xs text-content-2">
              <span className="truncate pr-2" title={m.note || undefined}>
                {fecha(m.created_at)} · {TIPO[m.type]}{m.reversed_at ? ' (devuelto)' : ''}{m.ticket_ref ? ` · ${m.ticket_ref}` : ''}
              </span>
              <span className={Number(m.amount) < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                {Number(m.amount) < 0 ? '−' : '+'}{pesos(Math.abs(Number(m.amount)))}
              </span>
            </li>
          ))}
        </ul>
      )}

      {abierto ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <div className="relative w-28">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-3 text-sm">$</span>
            <input type="number" min="0" step="0.01" value={monto} onChange={(e) => setMonto(e.target.value)} autoFocus
              className="w-full border border-line rounded-md pl-6 pr-2 py-1.5 text-sm" placeholder="0.00" disabled={guardando} />
          </div>
          <input type="text" value={nota} maxLength={200} onChange={(e) => setNota(e.target.value)}
            className="flex-1 min-w-[8rem] border border-line rounded-md px-2 py-1.5 text-sm" placeholder="Motivo (opcional)" disabled={guardando} />
          <button type="button" onClick={guardar} disabled={guardando}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50">
            {guardando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Cargar'}
          </button>
          <button type="button" onClick={() => setAbierto(false)} className="text-xs text-content-3 hover:underline">Cancelar</button>
        </div>
      ) : (
        <button type="button" onClick={() => setAbierto(true)}
          className="mt-2 inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md text-content-2 bg-surface-2 border border-line hover:bg-surface">
          <Plus className="h-3.5 w-3.5 mr-1" />Cargar saldo de regalo
        </button>
      )}
    </div>
  );
};

export default GiftBalanceCard;
